import type { Edge } from '@xyflow/react';
import type { JSONSchema7 } from 'json-schema';

import {
  getEntityEventSchema,
  getSchemaCache,
  type BusinessEventTriggerConfigType,
} from '../nodes/triggers/BusinessEventTrigger/schema';
import type { AutomationNode } from '../nodes/types';
import { isTriggerNodeType } from '../nodes/types';

/**
 * Standard trigger metadata schema.
 * Available as `trigger.*` in expressions.
 */
export const TRIGGER_METADATA_SCHEMA: JSONSchema7 = {
  type: 'object',
  title: 'Trigger',
  description: 'Information about the event that triggered this automation',
  properties: {
    id: {
      type: 'string',
      title: 'Trigger ID',
      description: 'Unique identifier for this trigger execution',
    },
    timestamp: {
      type: 'string',
      title: 'Timestamp',
      description: 'When the trigger was fired (ISO 8601 format)',
    },
    eventType: {
      type: 'string',
      title: 'Event Type',
      description: 'The type of event that triggered the automation',
    },
    entityId: {
      type: 'string',
      title: 'Entity ID',
      description: 'ID of the entity that triggered the event',
    },
    entityType: {
      type: 'string',
      title: 'Entity Type',
      description: 'Type of the entity (e.g., salesOrder, account)',
    },
  },
};

/**
 * Find the trigger node in the workflow
 */
export function findTriggerNode(
  nodes: AutomationNode[],
): AutomationNode | null {
  return nodes.find((n) => n.type && isTriggerNodeType(n.type)) ?? null;
}

/**
 * Find all upstream nodes (nodes that execute before the target node).
 * Uses BFS to traverse edges backwards from target to source.
 */
export function getUpstreamNodes(
  targetId: string,
  nodes: AutomationNode[],
  edges: Edge[],
): AutomationNode[] {
  const nodeMap = new Map(nodes.map((n) => [n.id, n]));
  const upstream: AutomationNode[] = [];
  const visited = new Set<string>();
  const queue: string[] = [];

  // Build reverse edge map (target -> sources)
  const reverseEdges = new Map<string, string[]>();
  for (const edge of edges) {
    const sources = reverseEdges.get(edge.target) ?? [];
    sources.push(edge.source);
    reverseEdges.set(edge.target, sources);
  }

  // BFS from target node backwards
  const initialSources = reverseEdges.get(targetId) ?? [];
  queue.push(...initialSources);

  while (queue.length > 0) {
    const nodeId = queue.shift();
    if (nodeId === undefined) continue;
    if (visited.has(nodeId)) continue;
    visited.add(nodeId);

    const node = nodeMap.get(nodeId);
    if (node) {
      upstream.push(node);
      const sources = reverseEdges.get(nodeId) ?? [];
      queue.push(...sources);
    }
  }

  return upstream;
}

/**
 * Resolve all $ref values in a schema inline.
 * This creates a copy of the schema with all references resolved.
 *
 * @param schema - The schema to resolve
 * @param schemaCache - Cache of loaded schemas for $ref lookup
 * @param visited - Set of visited $ref values to prevent infinite loops
 * @param maxDepth - Maximum depth to resolve (prevents deep recursion)
 */
function resolveSchemaRefs(
  schema: JSONSchema7,
  schemaCache: Record<string, JSONSchema7>,
  visited: Set<string> = new Set(),
  maxDepth = 10,
): JSONSchema7 {
  if (maxDepth <= 0) {
    return schema;
  }

  // If this schema is a $ref, resolve it
  if (schema.$ref) {
    const refId = schema.$ref;
    if (visited.has(refId)) {
      // Circular reference - return a simple placeholder
      return { type: 'object', title: refId.split('.')[0] };
    }

    const refSchema = schemaCache[refId];
    if (refSchema) {
      visited.add(refId);
      // Merge any additional properties from the referencing schema
      const { $ref: _, ...rest } = schema;
      const resolved = resolveSchemaRefs(
        refSchema,
        schemaCache,
        visited,
        maxDepth - 1,
      );
      return { ...resolved, ...rest };
    }
    // Reference not found - return a placeholder with type info
    return {
      type: 'string',
      title: refId.split('.').pop()?.replace('.jsonschema.json', ''),
    };
  }

  // Deep clone and resolve nested schemas
  const resolved: JSONSchema7 = { ...schema };

  // Resolve properties
  if (schema.properties) {
    resolved.properties = {};
    for (const [key, propDef] of Object.entries(schema.properties)) {
      if (typeof propDef === 'boolean') {
        resolved.properties[key] = propDef;
      } else {
        resolved.properties[key] = resolveSchemaRefs(
          propDef,
          schemaCache,
          new Set(visited),
          maxDepth - 1,
        );
      }
    }
  }

  // Resolve items (for arrays)
  if (schema.items) {
    if (Array.isArray(schema.items)) {
      resolved.items = schema.items.map((item) =>
        typeof item === 'boolean'
          ? item
          : resolveSchemaRefs(
              item,
              schemaCache,
              new Set(visited),
              maxDepth - 1,
            ),
      );
    } else if (typeof schema.items !== 'boolean') {
      resolved.items = resolveSchemaRefs(
        schema.items,
        schemaCache,
        new Set(visited),
        maxDepth - 1,
      );
    }
  }

  // Resolve anyOf/oneOf/allOf
  if (schema.anyOf) {
    resolved.anyOf = schema.anyOf.map((s) =>
      typeof s === 'boolean'
        ? s
        : resolveSchemaRefs(s, schemaCache, new Set(visited), maxDepth - 1),
    );
  }
  if (schema.oneOf) {
    resolved.oneOf = schema.oneOf.map((s) =>
      typeof s === 'boolean'
        ? s
        : resolveSchemaRefs(s, schemaCache, new Set(visited), maxDepth - 1),
    );
  }
  if (schema.allOf) {
    resolved.allOf = schema.allOf.map((s) =>
      typeof s === 'boolean'
        ? s
        : resolveSchemaRefs(s, schemaCache, new Set(visited), maxDepth - 1),
    );
  }

  // Resolve additionalProperties
  if (
    schema.additionalProperties &&
    typeof schema.additionalProperties === 'object'
  ) {
    resolved.additionalProperties = resolveSchemaRefs(
      schema.additionalProperties,
      schemaCache,
      new Set(visited),
      maxDepth - 1,
    );
  }

  return resolved;
}

/**
 * Get the data schema for a trigger node based on its configuration.
 * All schemas are pre-loaded via import.meta.glob, so this is synchronous.
 */
export function getTriggerDataSchema(triggerNode: AutomationNode): JSONSchema7 {
  if (triggerNode.type === 'BusinessEventTrigger') {
    const config = triggerNode.data.config as BusinessEventTriggerConfigType;
    const entityKey = config.source?.case;

    if (entityKey) {
      // Get the event envelope schema (e.g., SalesOrderEvent)
      // This is the correct schema structure: SalesOrderEvent -> created/updated/deleted -> SalesOrder
      const eventSchema = getEntityEventSchema(entityKey);

      if (eventSchema) {
        // Resolve all $ref values inline so the LSP worker can traverse the schema
        const schemaCache = getSchemaCache();
        const resolvedSchema = resolveSchemaRefs(eventSchema, schemaCache);

        return {
          ...resolvedSchema,
          title: 'Event Data',
          description: `Event payload for ${entityKey}`,
        };
      }
    }
  }

  // Default fallback schema for unknown trigger types or unconfigured triggers
  return {
    type: 'object',
    title: 'Event Data',
    description: 'Event data (configure trigger to see available fields)',
    properties: {},
  };
}

/**
 * Get the output schema for an action or flow control node.
 * Returns the schema that describes what this node outputs.
 */
export function getNodeOutputSchema(node: AutomationNode): JSONSchema7 | null {
  // For now, return basic output schemas based on node type
  // This can be expanded as action nodes get more sophisticated
  switch (node.type) {
    case 'HttpRequestAction':
      return {
        type: 'object',
        title: node.data.label || 'HTTP Response',
        description: 'Response from the HTTP request',
        properties: {
          statusCode: {
            type: 'number',
            title: 'Status Code',
            description: 'HTTP status code of the response',
          },
          body: {
            type: 'object',
            title: 'Body',
            description: 'Response body (parsed as JSON if applicable)',
          },
          headers: {
            type: 'object',
            title: 'Headers',
            description: 'Response headers',
          },
        },
      };

    case 'EmailAction':
      return {
        type: 'object',
        title: node.data.label || 'Email Result',
        description: 'Result of the email action',
        properties: {
          success: {
            type: 'boolean',
            title: 'Success',
            description: 'Whether the email was sent successfully',
          },
          messageId: {
            type: 'string',
            title: 'Message ID',
            description: 'Unique identifier for the sent message',
          },
        },
      };

    case 'Approval':
      return {
        type: 'object',
        title: node.data.label || 'Approval Result',
        description: 'Result of the approval',
        properties: {
          approved: {
            type: 'boolean',
            title: 'Approved',
            description: 'Whether the approval was granted',
          },
          approvedBy: {
            type: 'string',
            title: 'Approved By',
            description: 'User who approved or rejected',
          },
          timestamp: {
            type: 'string',
            title: 'Timestamp',
            description: 'When the approval decision was made',
          },
          comment: {
            type: 'string',
            title: 'Comment',
            description: 'Optional comment from the approver',
          },
        },
      };

    case 'Loop':
      return {
        type: 'object',
        title: node.data.label || 'Loop Context',
        description: 'Current loop iteration context',
        properties: {
          index: {
            type: 'number',
            title: 'Index',
            description: 'Current iteration index (0-based)',
          },
          item: {
            type: 'object',
            title: 'Item',
            description: 'Current item being processed',
          },
          isFirst: {
            type: 'boolean',
            title: 'Is First',
            description: 'Whether this is the first iteration',
          },
          isLast: {
            type: 'boolean',
            title: 'Is Last',
            description: 'Whether this is the last iteration',
          },
        },
      };

    // Flow control nodes that don't produce output
    case 'Condition':
    case 'Filter':
    case 'Switch':
    case 'Delay':
      return null;

    // Trigger nodes handled separately
    case 'BusinessEventTrigger':
    case 'HttpWebhookTrigger':
    case 'ScheduleTrigger':
      return null;

    default:
      return null;
  }
}

/**
 * Compute the combined JSON Schema for all scopes available at a node.
 * Returns a schema where each top-level property is a scope.
 * All schemas are pre-loaded via import.meta.glob, so this is synchronous.
 *
 * @param nodeId - The ID of the currently selected node
 * @param nodes - All nodes in the workflow
 * @param edges - All edges in the workflow
 * @returns JSONSchema7 - Combined scope schema
 */
export function computeScopeSchema(
  nodeId: string,
  nodes: AutomationNode[],
  edges: Edge[],
): JSONSchema7 {
  const properties: Record<string, JSONSchema7> = {};

  // 1. Find trigger node (always contributes to scope)
  const triggerNode = findTriggerNode(nodes);
  if (triggerNode) {
    properties['trigger'] = TRIGGER_METADATA_SCHEMA;
    properties['data'] = getTriggerDataSchema(triggerNode);
  }

  // 2. Find all upstream nodes (nodes that execute before this one)
  const upstreamNodes = getUpstreamNodes(nodeId, nodes, edges);

  // 3. Each upstream action node contributes its output to scope
  for (const node of upstreamNodes) {
    const outputSchema = getNodeOutputSchema(node);
    if (outputSchema) {
      // Use the node's label as the scope name, or fall back to node ID
      const scopeName = sanitizeScopeName(node.data.label || node.id);
      properties[scopeName] = outputSchema;
    }
  }

  return {
    type: 'object',
    properties,
  };
}

/**
 * Sanitize a label to be a valid identifier for use as a scope name.
 */
function sanitizeScopeName(label: string): string {
  return label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/^(\d)/, '_$1'); // Ensure doesn't start with number
}
